import { getUserStoresForRetailer, isRetailerClientEnabled, type RetailerType } from "../config/retailers";
import type { User } from "../services/UserService";
import { hasUserRetailerAccess } from "./userUtils";

/**
 * In-store retailers beyond Walmart and Home Depot that Plus/Pro plans unlock
 * (same list as pinto/utils/appLogic/moreStores.ts). Onboarding only picks
 * Walmart and Home Depot stores; after checkout, "Where else do you shop?"
 * offers these.
 */
export const EXTRA_IN_STORE_RETAILERS: RetailerType[] = [
  "target",
  "dollar_general",
  "lowes",
  "costco",
  "bestbuy",
  "tractor_supply",
  "northern_tool",
  "samsclub",
];

/** Extra in-store retailers this account can use but has no stores for yet. */
export function extraRetailersToSetUp(user: User | null | undefined): RetailerType[] {
  if (!user) return [];
  return EXTRA_IN_STORE_RETAILERS.filter(
    (retailer) =>
      isRetailerClientEnabled(retailer) &&
      hasUserRetailerAccess(user, retailer) &&
      getUserStoresForRetailer(user, retailer).length === 0
  );
}

/**
 * Only these retailers have their own categories field on the user. The others
 * share Walmart's "categories" key (saving would overwrite Walmart's choices)
 * or use a key the server rejects, so their store saves never send categories.
 */
const OWN_CATEGORY_FIELDS = new Set<string>(["walmart", "home_depot", "target", "dollar_general"]);

export function savesOwnCategories(retailer: string): boolean {
  return OWN_CATEGORY_FIELDS.has(retailer);
}

/**
 * Once a plan is active (SuccessPage), onboarding continues on pinto's
 * "You're in" screen, then "Where else do you shop?", then the deals.
 */
export const AFTER_CHECKOUT_PATH = "/onboarding/scanning";
export const MORE_STORES_PATH = "/onboarding/more-stores";

/** Where the post-checkout flow goes after "You're in". */
export function destinationAfterScanning(user: User | null | undefined): string {
  return extraRetailersToSetUp(user).length > 0 ? MORE_STORES_PATH : "/";
}
